import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { getProgramsByLift } from './data/programs';
import { getSettings, saveSettings } from './utils/settings';
import {
  getAllLogEntries, getAllBodyweights,
  getAllPRs,
  getProgramProgress, setProgramProgress
} from './db';
import ProgramImporter from './components/ProgramImporter';
import HomeTab from './components/HomeTab';
import ScheduleTab from './components/ScheduleTab';
import LogbookTab from './components/LogbookTab';
import ProgressTab from './components/ProgressTab';
import AccessoriesTab from './components/AccessoriesTab';
import ProfileTab from './components/ProfileTab';

// SVG Icons
const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
);
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const DumbbellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6.5 6.5h11M6 12h12"/>
    <rect x="2" y="8" width="4" height="8" rx="1"/>
    <rect x="18" y="8" width="4" height="8" rx="1"/>
    <rect x="5" y="6" width="2" height="12" rx="0.5"/>
    <rect x="17" y="6" width="2" height="12" rx="0.5"/>
  </svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

function SetupWizard({ onComplete }) {
  const [bench, setBench] = useState('');
  const [squat, setSquat] = useState('');
  const [deadlift, setDeadlift] = useState('');
  const [benchProg, setBenchProg] = useState('bench_2x_beg');
  const [squatProg, setSquatProg] = useState('squat_2x_beg');
  const [dlProg, setDlProg] = useState('dl_1x_beg');
  const [step, setStep] = useState(0);

  const handleFocus = (e) => e.target.select();

  const handleBlur = (setter) => (e) => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v) && v < 20) setter('20');
  };

  if (step === 0) {
    return (
      <div className="setup">
        <h1>SBS Powerlifting</h1>
        <p className="sub">Enter your current 1RMs to get started</p>
        <div className="field">
          <label>Bench Press 1RM (kg)</label>
          <input type="number" value={bench} onChange={e => setBench(e.target.value)}
            onFocus={handleFocus} onBlur={handleBlur(setBench)} placeholder="e.g. 100" />
        </div>
        <div className="field">
          <label>Squat 1RM (kg)</label>
          <input type="number" value={squat} onChange={e => setSquat(e.target.value)}
            onFocus={handleFocus} onBlur={handleBlur(setSquat)} placeholder="e.g. 140" />
        </div>
        <div className="field">
          <label>Deadlift 1RM (kg)</label>
          <input type="number" value={deadlift} onChange={e => setDeadlift(e.target.value)}
            onFocus={handleFocus} onBlur={handleBlur(setDeadlift)} placeholder="e.g. 180" />
        </div>
        <button className="btn btn-primary" onClick={() => {
          if (parseFloat(bench) >= 20 && parseFloat(squat) >= 20 && parseFloat(deadlift) >= 20) {
            setStep('path');
          }
        }}
        disabled={!(parseFloat(bench) >= 20 && parseFloat(squat) >= 20 && parseFloat(deadlift) >= 20)}>
          Next — Choose a Program
        </button>
      </div>
    );
  }

  // Choose between a well-known program and the SBS per-lift catalogue
  if (step === 'path') {
    return (
      <div className="setup">
        <h1>How do you want to train?</h1>
        <p className="sub">Both can be changed later from the Profile tab.</p>

        <div className="card" style={{ padding: 16, marginBottom: 10, cursor: 'pointer', border: '1px solid var(--accent)' }}
          onClick={() => setStep('popular')}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 17, fontWeight: 600 }}>
            Start with a popular program
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 6, lineHeight: 1.45 }}>
            Starting Strength, StrongLifts 5×5, Texas Method, Madcow, 5/3/1 — built
            automatically from your maxes, all three lifts set up in one tap.
            Recommended if you're new to structured training.
          </div>
        </div>

        <div className="card" style={{ padding: 16, marginBottom: 10, cursor: 'pointer' }}
          onClick={() => setStep(1)}>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 17, fontWeight: 600 }}>
            Pick SBS programs per lift
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 6, lineHeight: 1.45 }}>
            Choose an individual SBS program for each lift — different frequency and
            level per lift. For lifters who know what they want.
          </div>
        </div>

        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setStep(0)}>← Back</button>
      </div>
    );
  }

  // Popular-program path: reuse the importer with the not-yet-saved 1RMs
  if (step === 'popular') {
    const wizardSettings = {
      bench1RM: parseFloat(bench),
      squat1RM: parseFloat(squat),
      deadlift1RM: parseFloat(deadlift),
      rounding: 2.5,
    };
    return (
      <div className="setup" style={{ paddingTop: 16 }}>
        <ProgramImporter
          settings={wizardSettings}
          embedded
          onImport={async (generated) => {
            saveSettings({
              ...wizardSettings,
              benchProgram: generated.bench.id,
              squatProgram: generated.squat.id,
              deadliftProgram: generated.deadlift.id,
              setupComplete: true,
            });
            for (const lift of ['bench', 'squat', 'deadlift']) {
              await setProgramProgress(lift, { weekIndex: 0, dayIndex: 0, cycle: 1 });
            }
            onComplete();
          }}
          onCancel={() => setStep('path')}
        />
      </div>
    );
  }

  return (
    <div className="setup">
      <h1>Select Programs</h1>
      <p className="sub">Choose one program per lift. You can change these later.</p>

      <div className="section-title">Bench Press</div>
      <select value={benchProg} onChange={e => setBenchProg(e.target.value)}>
        {getProgramsByLift('bench').map(p => (
          <option key={p.id} value={p.id}>{p.displayName}</option>
        ))}
      </select>

      <div className="section-title" style={{marginTop:20}}>Squat</div>
      <select value={squatProg} onChange={e => setSquatProg(e.target.value)}>
        {getProgramsByLift('squat').map(p => (
          <option key={p.id} value={p.id}>{p.displayName}</option>
        ))}
      </select>

      <div className="section-title" style={{marginTop:20}}>Deadlift</div>
      <select value={dlProg} onChange={e => setDlProg(e.target.value)}>
        {getProgramsByLift('deadlift').map(p => (
          <option key={p.id} value={p.id}>{p.displayName}</option>
        ))}
      </select>

      <button className="btn btn-primary" style={{marginTop:28}} onClick={async () => {
        const benchMax = parseFloat(bench);
        const squatMax = parseFloat(squat);
        const dlMax = parseFloat(deadlift);
        const round = 2.5;
        saveSettings({
          bench1RM: benchMax,
          squat1RM: squatMax,
          deadlift1RM: dlMax,
          benchProgram: benchProg,
          squatProgram: squatProg,
          deadliftProgram: dlProg,
          setupComplete: true,
        });
        const perDayProgs = ['bench_3x_beg', 'squat_3x_beg'];
        const perDayPcts = { 0: 0.70, 1: 0.75, 2: 0.80 };
        const progMap = { bench: benchProg, squat: squatProg, deadlift: dlProg };
        const maxMap = { bench: benchMax, squat: squatMax, deadlift: dlMax };
        for (const lift of ['bench', 'squat', 'deadlift']) {
          const prog = { weekIndex: 0, dayIndex: 0, cycle: 1 };
          if (perDayProgs.includes(progMap[lift])) {
            prog.dayWeights = {};
            for (const [d, pct] of Object.entries(perDayPcts)) {
              prog.dayWeights[d] = Math.floor((maxMap[lift] * pct) / round) * round;
            }
          }
          await setProgramProgress(lift, prog);
        }
        onComplete();
      }}>
        Start Training
      </button>
      <button className="btn btn-secondary" style={{marginTop:8}} onClick={() => setStep('path')}>← Back</button>
    </div>
  );
}

export default function App() {
  const [settings, setSettings] = useState(getSettings);
  const [tab, setTab] = useState('home');
  const [logEntries, setLogEntries] = useState([]);
  const [bodyweights, setBodyweights] = useState([]);
  const [prs, setPRs] = useState([]);
  const [progress, setProgress] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    async function load() {
      const [logs, bws, allPRs] = await Promise.all([
        getAllLogEntries(),
        getAllBodyweights(),
        getAllPRs(),
      ]);
      setLogEntries(logs);
      setBodyweights(bws);
      setPRs(allPRs);

      const prog = {};
      for (const lift of ['bench', 'squat', 'deadlift']) {
        prog[lift] = await getProgramProgress(lift);
      }
      setProgress(prog);
      setSettings(getSettings());
    }
    load();
  }, [refreshKey]);

  if (!settings.setupComplete) {
    return <SetupWizard onComplete={refresh} />;
  }

  const sharedProps = {
    settings,
    logEntries,
    bodyweights,
    prs,
    progress,
    refresh,
    setTab,
  };

  return (
    <div className="app">
      <div className="tab-content">
        {tab === 'home' && <HomeTab {...sharedProps} />}
        {tab === 'schedule' && <ScheduleTab {...sharedProps} />}
        {tab === 'logbook' && <LogbookTab {...sharedProps} />}
        {tab === 'progress' && <ProgressTab {...sharedProps} />}
        {tab === 'accessories' && <AccessoriesTab {...sharedProps} />}
        {tab === 'profile' && <ProfileTab {...sharedProps} />}
      </div>
      <nav className="tab-bar">
        <div className="tab-bar-inner">
          {[
            { id: 'home', icon: HomeIcon, label: 'Home' },
            { id: 'schedule', icon: CalendarIcon, label: 'Schedule' },
            { id: 'logbook', icon: BookIcon, label: 'Logbook' },
            { id: 'progress', icon: ChartIcon, label: 'Progress' },
            { id: 'accessories', icon: DumbbellIcon, label: 'Acc.' },
            { id: 'profile', icon: UserIcon, label: 'Profile' },
          ].map(t => (
            <button key={t.id} className={`tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}>
              <t.icon />
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
